import React, { useContext, useEffect, useState } from "react";
import { Row, Spinner } from "reactstrap";
import { useSelector } from "react-redux";

import BizinBackend from "../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../Hooks/useGetCsrfToken";
import { BI_WS0025Context } from "../context";

import CardContext from "../../../components/Common/CardContext";
import CardFlow from "../../../components/Common/CardFlow";
import CardContextExpansive from "../../../components/Common/CardContextExpansive";
import TableSimple from "../../../components/Dynamic/TableSimple";

const COLUMNS_AGENDAMENTO = [
  {
    field: "Tipo_Documento",
    header: "Tipo Documento",
    sort: true,
  },
  {
    field: "Numero_Documento",
    header: "Nº Documento",
    sort: true,
  },
  {
    field: "Peso_Bruto",
    header: "Peso Bruto (kg)",
    sort: true,
    align: "right",
  },
  {
    field: "Volume",
    header: "Volume",
    sort: true,
  },
  {
    field: "Valor",
    header: "Valor (R$)",
    sort: true,
    align: "right",
  },
];

const COLUMNS_PRE_AGENDAMENTO = [
  {
    field: "Numero_Pedido",
    header: "Número Pedido",
    sort: true,
  },
  {
    field: "Numero_Item",
    header: "Número Item",
    sort: true,
  },
  {
    field: "Peso_Bruto",
    header: "P.B. (kg)",
    sort: true,
    align: "right",
  },
  {
    field: "Quantidade",
    header: "Quantidade",
    sort: true,
    align: "right",
  },
  {
    field: "Valor",
    header: "Valor (R$)",
    sort: true,
    align: "right",
  },
];

const toBoolean = (value) => value === true || value === 1 || value === "1";

function GridBody() {
  const { parseParams, screenConfig, setScreenConfig, setFilteredData, filteredData } =
    useContext(BI_WS0025Context);

  const [dataContext, setDataContext] = useState([]);
  const [dataContextExpansive, setDataContextExpansive] = useState([]);
  const [dataFlow, setDataFlow] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [observacoes, setObservacoes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const ID_Arquivo = parseParams.get("ID_Arquivo");

  const isPreAgendamento = screenConfig?.Tipo_Tela === "PRE_AGENDAMENTO";

  const columnsList = isPreAgendamento
    ? COLUMNS_PRE_AGENDAMENTO
    : COLUMNS_AGENDAMENTO;

  const { userProfile, access_token } = useSelector((state) => ({
    userProfile: state.login.profile,
    access_token: state.login.user.access_token,
  }));

  const csrfToken = useGetCsrfToken();

  const handleData = async () => {
    try {
      setIsLoading(true);

      setDataFlow([]);
      setDataContext([]);
      setDataContextExpansive([]);
      setDataTable([]);
      setObservacoes("");

      const result = await BizinBackend.executeProcedure(
        {
          1: userProfile.ID_Usuario,
          2: "pt-BR",
          3: ID_Arquivo,
        },
        "FRONT_Agendamento_Obter_Resumo",
        csrfToken,
        access_token,
      );

      const data = result?.data || result;

      const resultsetFlow = Array.isArray(data?.resultset_1)
        ? data.resultset_1
        : [];

      const resultsetContext = Array.isArray(data?.resultset_2)
        ? data.resultset_2
        : [];

      const resultsetTable = Array.isArray(data?.resultset_4)
        ? data.resultset_4
        : [];


      const mappedFlowData = resultsetFlow.map((item) => ({
        name: item.Nome_Empresa,
          description: item.Descricao,
          logo: item.Logo_Empresa,
      }));

      setDataFlow(mappedFlowData);

      const mappedContextData = resultsetContext.map((item) => ({
        label: item.Coluna,
        value: item.Valor,
      }));

      setDataContext(mappedContextData);

      let formatterContextExpansive = [];

      try {
        const dados = data?.resultset_3?.[0]?.Dados;

        if (dados) {
          const parsed = typeof dados === "string" ? JSON.parse(dados) : dados;

          if (Array.isArray(parsed) && parsed.length > 0) {
            formatterContextExpansive = Array.isArray(parsed[0])
              ? parsed
              : [parsed];
          }
        }
      } catch (error) {
        console.log("Erro ao formatar dados expansivos:", error);
      }

      setDataContextExpansive(formatterContextExpansive);
      setDataTable(resultsetTable);
      setObservacoes(data?.resultset_5?.[0]?.Observacoes || "");

      const config = data?.resultset_6?.[0];
      const exibirQRCode = toBoolean(config?.Exibir_QRCode);
      const exibirExpansivo = toBoolean(config?.Exibir_Expansivo);

      setFilteredData({
        dataTable: resultsetTable,
        contextData: mappedContextData,
        flowData: mappedFlowData,
        contextColapseData: exibirExpansivo ? formatterContextExpansive : [],
        enableContextColapseQRCode: exibirQRCode,
        title: "Protocolo de Agendamento " + config.ID_Agendamento,
        tableTitle: "Detalhes da Entrega"
      })

      if (config) {
        setScreenConfig({
          Protocolo: config.Protocolo || "",
          ID_Agendamento: config.ID_Agendamento || null,
          ID_Tipo_Agendamento: config.ID_Tipo_Agendamento || null,
          Tipo_Tela: config.Tipo_Tela || "",
          Exibir_QRCode: exibirQRCode,
          Exibir_Expansivo: exibirExpansivo,
        });
      }
    } catch (error) {
      console.log("Erro ao buscar dados do protocolo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ID_Arquivo) {
      handleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ID_Arquivo]);

  return (
    <>
      {isLoading && (
        <div className="loading-overlay">
          <Spinner color="primary" />
        </div>
      )}

      <Row className="align-items-center">
        {dataFlow.length > 0 && (
          <div className="mb-2 col-12 col-lg-auto">
            <CardFlow flowData={dataFlow} />
          </div>
        )}

        {dataContext.length > 0 && (
          <div className="mb-3 col">
            <CardContext data={dataContext} />
          </div>
        )}

        {screenConfig?.Exibir_Expansivo && dataContextExpansive.length > 0 && (
          <div className="col-12 mb-3">
            <CardContextExpansive
              data={dataContextExpansive}
              enableQRCode={screenConfig?.Exibir_QRCode}
            />
          </div>
        )}

        <div className="col-12 w-100">
          <TableSimple
            data={dataTable}
            dataKey="Chave_Registro"
            columnsList={columnsList}
            rowClassName={(rowData) =>
              rowData.Tipo_Documento === "Total" ? "row-total-agendamento" : ""
            }
          />
        </div>

        {observacoes.length > 0 && (
          <div className="col-12">
            <div
              className="d-flex rounded overflow-hidden"
              style={{
                width: "70%",
                marginTop: "-66px",
                height: "50px",
                position: "relative",
                zIndex: 1,
                background: "#F1F1F1",
              }}
            >
              <div
                className="px-4 d-flex align-items-center fw-semibold text-color-azul2"
                style={{
                  minWidth: 150,
                  background: "#D9D9D9",
                }}
              >
                Observações
              </div>

              <div
                className="px-4 py-4 font-size-12 text-color-cinza5"
                style={{
                  background: "#f5f5f5",
                  width: "100%",
                }}
              >
                {observacoes || ""}
              </div>
            </div>
          </div>
        )}
      </Row>
    </>
  );
}

export default GridBody;
